import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import {
  createCourierControl,
  deleteCourierControl,
  getCourierControl,
  listCourierControl,
  updateCourierControl,
} from "../../api/CourierControl/courierControlApi";
import { getUserById } from "../../api/User/userApi";
import { socket } from "../../socket";
import {
  canCourierAccess,
  canCourierSolicitar,
  canCourierTerminar,
} from "../../utils/courierPermissions";
import {
  pageContentCardSx,
  pageFieldSx,
  pageHeroShellSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import CourierControlDialog, {
  emptyEnvio,
  emptyLine,
} from "./CourierControlDialog";

const fmtNum = (value, digits = 2) => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const fmtDate = (value) => {
  const raw = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
};

const fmtBoxMeasures = (row = {}) => {
  const L = Number(row.LargoCm);
  const W = Number(row.AnchoCm);
  const H = Number(row.AltoCm);
  if (![L, W, H].every((n) => Number.isFinite(n) && n > 0)) return "—";
  return `${fmtNum(L, 1)} × ${fmtNum(W, 1)} × ${fmtNum(H, 1)} cm`;
};

const statusChipSx = (theme, estado) => {
  const map = {
    PEDIDO: theme.palette.info.main,
    PREPARANDO_ADUANAS: theme.palette.secondary?.main || "#7b1fa2",
    EN_CURSO: theme.palette.warning.main,
    ENTREGADO: theme.palette.success.main,
    CANCELADO: theme.palette.error.main,
  };
  const color = map[estado] || theme.palette.info.main;
  return {
    ...pageKpiChipSx,
    height: 26,
    fontWeight: 800,
    bgcolor: alpha(color, 0.14),
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
  };
};

const directionChipSx = (theme, direccion) => {
  const color =
    direccion === "ENTRADA"
      ? theme.palette.secondary?.main || "#7b1fa2"
      : theme.palette.primary.main;
  return {
    ...pageKpiChipSx,
    height: 26,
    fontWeight: 700,
    bgcolor: alpha(color, 0.12),
    color,
    border: `1px solid ${alpha(color, 0.4)}`,
  };
};

const filterToggleSx = (theme) => ({
  borderRadius: "999px !important",
  border: `1px solid ${alpha(theme.palette.divider, 0.7)} !important`,
  overflow: "hidden",
  bgcolor: alpha(
    theme.palette.common.black,
    theme.palette.mode === "dark" ? 0.2 : 0.03,
  ),
  height: 34,
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.72rem",
    px: 1.25,
    py: 0,
    height: 34,
    border: "none !important",
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      bgcolor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": {
        bgcolor: theme.palette.primary.dark,
      },
    },
  },
});

const tableHeadCellSx = (theme) => ({
  fontWeight: 800,
  fontSize: "0.68rem",
  letterSpacing: 0.35,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  bgcolor: `${theme.palette.background.paper} !important`,
  backgroundColor: `${theme.palette.background.paper} !important`,
  backgroundImage: "none !important",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
  whiteSpace: "nowrap",
  py: 1.25,
  zIndex: 3,
  top: 0,
  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.9)}`,
});

export default function CourierControl() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { contentMargin } = useContext(SidebarContext);
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [direccion, setDireccion] = useState("");
  const [ambito, setAmbito] = useState("");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(emptyEnvio());
  const [saving, setSaving] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const decoded = token ? jwtDecode(token) : null;
        const userId = decoded?.userId || decoded?.id;
        if (!userId) {
          if (!cancelled) {
            setPermUser({ isAdmin: !!decoded?.isAdmin });
            setPermsLoaded(true);
          }
          return;
        }
        const user = await getUserById(userId);
        if (!cancelled) {
          setPermUser({
            ...(user || {}),
            isAdmin: user?.isAdmin ?? decoded?.isAdmin,
          });
          setPermsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPermUser({});
          setPermsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allowAccess = canCourierAccess(permUser || {});
  const allowSolicitar = canCourierSolicitar(permUser || {});
  const allowTerminar = canCourierTerminar(permUser || {});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listCourierControl({
        direccion: direccion || undefined,
        ambito: ambito || undefined,
        q: q.trim() || undefined,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.loadError"));
    } finally {
      setLoading(false);
    }
  }, [direccion, ambito, q, t]);

  useEffect(() => {
    if (!permsLoaded || !allowAccess) return undefined;
    const timer = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, q, permsLoaded, allowAccess]);

  // Tiempo real China ↔ España
  useEffect(() => {
    if (!permsLoaded || !allowAccess) return undefined;
    const onCourier = () => {
      load();
    };
    socket.on("courier-control-updated", onCourier);
    return () => {
      socket.off("courier-control-updated", onCourier);
    };
  }, [permsLoaded, allowAccess, load]);

  // Si el diálogo está abierto, refrescar el draft cuando cambia ese envío
  useEffect(() => {
    if (!dialogOpen || !draft?.IdCourierControl) return undefined;
    const id = Number(draft.IdCourierControl);
    const onCourier = async (payload) => {
      if (Number(payload?.IdCourierControl) !== id) return;
      if (payload?.action === "delete") {
        setDialogOpen(false);
        return;
      }
      try {
        const envio = await getCourierControl(id);
        setDraft({
          ...emptyEnvio(),
          ...envio,
          Fecha: String(envio.Fecha || "").slice(0, 10),
          lineas: envio.lineas?.length
            ? envio.lineas.map((linea) => emptyLine(linea))
            : [emptyLine()],
        });
      } catch {
        /* ignore */
      }
    };
    socket.on("courier-control-updated", onCourier);
    return () => socket.off("courier-control-updated", onCourier);
  }, [dialogOpen, draft?.IdCourierControl]);

  const openDraft = (envio) => {
    setDraft({
      ...emptyEnvio(),
      ...envio,
      Fecha: String(envio.Fecha || "").slice(0, 10),
      lineas: envio.lineas?.length
        ? envio.lineas.map((linea) => emptyLine(linea))
        : [emptyLine()],
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!permsLoaded || !allowAccess) return;
    const nueva = params.get("nueva");
    const id = params.get("id");
    const muestraId = params.get("muestraId");
    const codigo = params.get("codigo") || "";

    if (nueva === "1") {
      if (!allowSolicitar) {
        setParams({}, { replace: true });
        return;
      }
      openDraft(
        emptyEnvio({
          lineas: [
            emptyLine({
              IdMuestra: muestraId ? Number(muestraId) : null,
              CodigoMuestra: codigo,
            }),
          ],
        }),
      );
      setParams({}, { replace: true });
      return;
    }

    if (id) {
      getCourierControl(id)
        .then((envio) => openDraft(envio))
        .catch(() => setError(t("courierControl.loadError")));
      setParams({}, { replace: true });
    }
  }, [params, setParams, t, permsLoaded, allowAccess, allowSolicitar]);

  const save = async (payloadOverride = null) => {
    // onClick pasa el evento; solo aceptar override explícito de envío
    const isEnvioOverride =
      payloadOverride &&
      typeof payloadOverride === "object" &&
      !payloadOverride.nativeEvent &&
      (Object.prototype.hasOwnProperty.call(payloadOverride, "Estado") ||
        Object.prototype.hasOwnProperty.call(payloadOverride, "lineas") ||
        Object.prototype.hasOwnProperty.call(
          payloadOverride,
          "IdCourierControl",
        ) ||
        Object.prototype.hasOwnProperty.call(payloadOverride, "NumeroAwb"));
    const payload = isEnvioOverride ? { ...draft, ...payloadOverride } : draft;
    const prevEstado = String(draft.Estado || "PEDIDO").toUpperCase();
    const nextEstado = String(payload.Estado || "PEDIDO").toUpperCase();
    const confirmingShip =
      isEnvioOverride &&
      allowSolicitar &&
      !allowTerminar &&
      prevEstado === "PEDIDO" &&
      nextEstado === "EN_CURSO";
    const draftAlreadyLocked =
      !allowTerminar &&
      (prevEstado === "PREPARANDO_ADUANAS" ||
        prevEstado === "EN_CURSO" ||
        prevEstado === "ENTREGADO" ||
        prevEstado === "CANCELADO");
    const canCreate = allowSolicitar;
    const canUpdate =
      allowTerminar ||
      (allowSolicitar && (!draftAlreadyLocked || confirmingShip));
    if (payload.IdCourierControl) {
      if (!canUpdate) {
        setError(t("courierControl.saveError"));
        return;
      }
    } else if (!canCreate) {
      setError(t("courierControl.saveError"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (payload.IdCourierControl) {
        await updateCourierControl(payload.IdCourierControl, payload);
      } else {
        await createCourierControl(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft.IdCourierControl || !allowTerminar) return;
    if (!window.confirm(t("courierControl.deleteConfirm"))) return;
    setSaving(true);
    try {
      await deleteCourierControl(draft.IdCourierControl);
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const directionLabel = (value) =>
    value === "ENTRADA" ? t("courierControl.entry") : t("courierControl.exit");
  const scopeLabel = (value) =>
    value === "INTERNACIONAL"
      ? t("courierControl.international")
      : t("courierControl.national");
  const statusLabel = (value) => {
    if (value === "EN_CURSO") return t("courierControl.statusEnCurso");
    if (value === "PREPARANDO_ADUANAS")
      return t("courierControl.statusPreparandoAduanas");
    if (value === "ENTREGADO") return t("courierControl.statusEntregado");
    if (value === "CANCELADO") return t("courierControl.statusCancelado");
    return t("courierControl.statusPedido");
  };

  const kpis = useMemo(() => {
    const counts = {
      total: rows.length,
      PEDIDO: 0,
      PREPARANDO_ADUANAS: 0,
      EN_CURSO: 0,
      ENTREGADO: 0,
    };
    rows.forEach((row) => {
      if (counts[row.Estado] != null) counts[row.Estado] += 1;
    });
    return counts;
  }, [rows]);

  const openRow = (id) => {
    getCourierControl(id)
      .then(openDraft)
      .catch(() => setError(t("courierControl.loadError")));
  };

  if (permsLoaded && !allowAccess) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t("courierControl.noPermission")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...contentMargin,
        p: { xs: 1.5, md: 2.5 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        height: "100dvh",
        minHeight: "100dvh",
        maxHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 28%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 32%)`,
      }}
    >
      <Box sx={{ flexShrink: 0, mb: 1.5 }}>
        <Paper
          elevation={0}
          sx={{
            ...pageHeroShellSx(theme),
            mb: 0,
            backdropFilter: "none",
            overflow: "hidden",
            background:
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : "linear-gradient(135deg, #eef8f9 0%, #f7f4ff 52%, #f3faf6 100%)",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              py: { xs: 1.1, md: 1.25 },
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
                flex: { xs: "1 1 auto", md: "0 1 auto" },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.secondary?.main || "#7b1fa2", 0.16)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.45)}`,
                }}
              >
                <LocalShippingOutlinedIcon
                  sx={{ color: "primary.main", fontSize: 22 }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{
                    letterSpacing: 1.2,
                    display: "block",
                    fontWeight: 800,
                    lineHeight: 1,
                    mb: 0.2,
                    fontSize: "0.65rem",
                  }}
                >
                  {t("courierControl.overline")}
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    fontSize: { xs: "1.15rem", md: "1.35rem" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("courierControl.title")}
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              flexWrap="wrap"
              gap={0.75}
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 auto" },
                minWidth: 0,
                alignItems: "center",
              }}
            >
              <Chip
                size="small"
                label={t("courierControl.kpiTotal", { count: kpis.total })}
                sx={{
                  ...pageKpiChipSx,
                  height: 26,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  fontWeight: 800,
                }}
              />
              <Chip
                size="small"
                label={`${kpis.PEDIDO} ${t("courierControl.statusPedido")}`}
                sx={{ ...statusChipSx(theme, "PEDIDO"), height: 26 }}
              />
              {kpis.PREPARANDO_ADUANAS ? (
                <Chip
                  size="small"
                  label={`${kpis.PREPARANDO_ADUANAS} ${t("courierControl.statusPreparandoAduanas")}`}
                  sx={{
                    ...statusChipSx(theme, "PREPARANDO_ADUANAS"),
                    height: 26,
                  }}
                />
              ) : null}
              <Chip
                size="small"
                label={`${kpis.EN_CURSO} ${t("courierControl.statusEnCurso")}`}
                sx={{ ...statusChipSx(theme, "EN_CURSO"), height: 26 }}
              />
              <Chip
                size="small"
                label={`${kpis.ENTREGADO} ${t("courierControl.statusEntregado")}`}
                sx={{ ...statusChipSx(theme, "ENTREGADO"), height: 26 }}
              />
            </Stack>

            {allowSolicitar ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => openDraft(emptyEnvio())}
                sx={{
                  ...pagePrimaryButtonSx(theme),
                  ml: { md: "auto" },
                  alignSelf: "center",
                  whiteSpace: "nowrap",
                  px: 1.75,
                  py: 0.85,
                  borderRadius: 999,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.28)}`,
                }}
              >
                {t("courierControl.newRequest")}
              </Button>
            ) : null}
          </Box>

          <Box
            sx={{
              mx: { xs: 1.25, md: 1.5 },
              mb: { xs: 1.25, md: 1.5 },
              px: { xs: 1, md: 1.25 },
              py: 1,
              borderRadius: 2.5,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.04)
                  : alpha(theme.palette.common.white, 0.72),
              border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
              boxShadow: `inset 0 1px 0 ${alpha("#fff", theme.palette.mode === "dark" ? 0.04 : 0.9)}`,
            }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={direccion}
              onChange={(_, value) => setDireccion(value || "")}
              sx={filterToggleSx(theme)}
            >
              <ToggleButton value="">{t("courierControl.all")}</ToggleButton>
              <ToggleButton value="SALIDA">
                {t("courierControl.exit")}
              </ToggleButton>
              <ToggleButton value="ENTRADA">
                {t("courierControl.entry")}
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={ambito}
              onChange={(_, value) => setAmbito(value || "")}
              sx={filterToggleSx(theme)}
            >
              <ToggleButton value="">{t("courierControl.all")}</ToggleButton>
              <ToggleButton value="NACIONAL">
                {t("courierControl.national")}
              </ToggleButton>
              <ToggleButton value="INTERNACIONAL">
                {t("courierControl.international")}
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder={t("courierControl.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{
                ...pageFieldSx(theme),
                minWidth: { xs: "100%", sm: 200 },
                flex: { sm: "1 1 220px" },
                maxWidth: { md: 360 },
                ml: { md: "auto" },
                "& .MuiInputBase-root": {
                  ...pageFieldSx(theme)["& .MuiInputBase-root"],
                  borderRadius: 999,
                  height: 34,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.paper, 0.85)
                      : "#fff",
                },
                "& .MuiInputBase-input": {
                  py: 0.5,
                  fontSize: "0.82rem",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mt: 1.5, mb: 0, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...pageContentCardSx(theme),
          p: 0,
          mt: 0,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "background.paper",
          backgroundImage: "none",
          background: theme.palette.background.paper,
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "auto",
            maxWidth: "100%",
            bgcolor: "background.paper",
            "& .MuiTableCell-stickyHeader": {
              bgcolor: `${theme.palette.background.paper} !important`,
              backgroundColor: `${theme.palette.background.paper} !important`,
              backgroundImage: "none !important",
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: "background.paper",
                  "& th": {
                    bgcolor: `${theme.palette.background.paper} !important`,
                    backgroundImage: "none !important",
                  },
                }}
              >
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.date")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.direction")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.origin")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.destination")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.packages")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.boxMeasures")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.grossWeight")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.createdBy")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.content")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.pieces")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.volume")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.volWeight")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.chargeable")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.cost")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.realCost")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.difference")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.courierCompany")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.awb")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.status")}
                </TableCell>
                <TableCell sx={{ ...tableHeadCellSx(theme), width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center" sx={{ py: 6 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <LocalShippingOutlinedIcon
                        sx={{ fontSize: 36, opacity: 0.45 }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {t("courierControl.empty")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const id = row.IdCourierControl;
                  const open = expandedIds.has(id);
                  const lineas = Array.isArray(row.contenidoLineas)
                    ? row.contenidoLineas
                    : [];
                  const hasLines = lineas.length > 0 || Number(row.piezas) > 0;
                  const diff = Number(row.Diferencia);
                  const diffColor =
                    Number.isFinite(diff) && diff !== 0
                      ? diff > 0
                        ? theme.palette.error.main
                        : theme.palette.success.main
                      : "inherit";
                  return (
                    <React.Fragment key={id}>
                      <TableRow
                        hover
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                          "& td": {
                            borderBottom: open
                              ? "none"
                              : `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                            py: 1.15,
                          },
                        }}
                        onClick={() => openRow(id)}
                      >
                        <TableCell
                          sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                        >
                          {fmtDate(row.Fecha)}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" gap={0.75} flexWrap="wrap">
                            <Chip
                              size="small"
                              label={directionLabel(row.Direccion)}
                              sx={directionChipSx(theme, row.Direccion)}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={scopeLabel(row.Ambito)}
                              sx={{
                                ...pageKpiChipSx,
                                height: 26,
                                fontWeight: 600,
                              }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={row.Origen || ""}
                          >
                            {row.Origen || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            title={row.Destino || ""}
                          >
                            {row.Destino || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {row.bultos != null ? row.bultos : "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fmtBoxMeasures(row)}
                          </Typography>
                          {row.TipoEmbalaje ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {row.TipoEmbalaje}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {row.PesoBrutoKg != null && row.PesoBrutoKg !== ""
                            ? `${fmtNum(row.PesoBrutoKg, 3)} kg`
                            : "—"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 120 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={row.CreatedByName || row.SolicitadoPor || ""}
                          >
                            {row.CreatedByName || row.SolicitadoPor || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, minWidth: 140 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={
                              Array.isArray(row.contenidoLabels) &&
                              row.contenidoLabels.length
                                ? row.contenidoLabels.join(", ")
                                : row.contenido || ""
                            }
                            sx={{ fontWeight: 600 }}
                          >
                            {row.contenido || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{row.piezas || 0}</TableCell>
                        <TableCell align="right">
                          {fmtNum(row.VolumenM3, 4)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtNum(row.PesoVolumetricoKg, 3)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmtNum(row.PesoFacturableKg, 3)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtNum(row.Coste, 2)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtNum(row.CosteReal, 2)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, color: diffColor }}
                        >
                          {fmtNum(row.Diferencia, 2)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 140 }}>
                          <Typography variant="body2" noWrap fontWeight={600}>
                            {row.CompaniaCourier || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.NumeroAwb ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "ui-monospace, monospace",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                              }}
                            >
                              {row.NumeroAwb}
                            </Typography>
                          ) : row.Estado === "PEDIDO" ? (
                            <Chip
                              size="small"
                              label={t("courierControl.pendingBox")}
                              sx={statusChipSx(theme, "PEDIDO")}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusLabel(row.Estado)}
                            sx={statusChipSx(theme, row.Estado)}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ pl: 0, width: 48 }}>
                          <IconButton
                            size="small"
                            aria-label={
                              open
                                ? t("courierControl.collapseContent")
                                : t("courierControl.expandContent")
                            }
                            disabled={!hasLines}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(id);
                            }}
                          >
                            {open ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={20}
                          sx={{
                            py: 0,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                          }}
                        >
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                px: 2,
                                py: 1.25,
                                mr: 1,
                                mb: 1,
                                borderRadius: 2,
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04,
                                ),
                                border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.4,
                                  color: "text.secondary",
                                  display: "block",
                                  mb: 0.75,
                                }}
                              >
                                {t("courierControl.contentLines", {
                                  count: lineas.length || row.piezas || 0,
                                })}
                              </Typography>
                              {lineas.length ? (
                                <Box
                                  component="ul"
                                  sx={{
                                    m: 0,
                                    p: 0,
                                    listStyle: "none",
                                  }}
                                >
                                  {lineas.map((linea, idx) => (
                                    <Box
                                      component="li"
                                      key={`${id}-${idx}-${linea.label}`}
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 1.5,
                                        py: 0.55,
                                        borderBottom:
                                          idx < lineas.length - 1
                                            ? `1px solid ${alpha(theme.palette.divider, 0.4)}`
                                            : "none",
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700 }}
                                      >
                                        {linea.label}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ flexShrink: 0 }}
                                      >
                                        {[
                                          linea.Estilo &&
                                          linea.CodigoMuestra &&
                                          linea.label !== linea.CodigoMuestra
                                            ? linea.CodigoMuestra
                                            : null,
                                          linea.PesoKg != null &&
                                          Number.isFinite(Number(linea.PesoKg))
                                            ? `${fmtNum(linea.PesoKg, 3)} kg`
                                            : null,
                                          linea.Motivo,
                                        ]
                                          .filter(Boolean)
                                          .join(" · ")}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {row.contenido || "—"}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CourierControlDialog
        open={dialogOpen}
        envio={draft}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onChange={setDraft}
        onSave={save}
        onDelete={remove}
        canSolicitar={allowSolicitar}
        canTerminar={allowTerminar}
        currentUser={permUser}
      />
    </Box>
  );
}
